import React, { useState, useCallback, useContext, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';

import { emptyArray } from 'ui-components/default-props/empty';

import getCriteriaArguments from './get-criteria-arguments';
import style from './style.module.less';
import FilterBar from '../../../../components/filter-bar';
import { criteriaOperatorsMap } from '../../../../containers/base-filter-bar-container/criteria-operators';
import { FiltersContext } from '../../../../filters/context';
import { mapOrdersFilterBarFiltersToContext } from '../../../../filters/context/map-filter-bar-filters-to-context';
import { useSortedTranslatedCountries } from '../../../../hooks/use-sorted-translated-countries';

const SOURCE = 'filterBar';

const BatchesFilterBar = ({ isHidden, allFilterBys = emptyArray, defaultFilters = emptyArray }) => {
  const { replaceFiltersForSource } = useContext(FiltersContext);
  const baseCriteriaArguments = useSelector(getCriteriaArguments);
  const [appliedFilterBarFilters, setAppliedFilterBarFilters] = useState(defaultFilters);

  const countries = useSortedTranslatedCountries({ labelProp: 'displayName' });
  const correctedCriteriaArgumentsMap = useMemo(() => {
    const correctedCounties = countries.map((country) => ({
      ...country,
      id: 'option-country',
      value: country.code,
    }));
    return { ...baseCriteriaArguments, country: correctedCounties };
  }, [baseCriteriaArguments, countries]);

  const _onFiltersChanged = useCallback(
    (filterBarFilters) => {
      setAppliedFilterBarFilters(filterBarFilters);
      const newFilters = mapOrdersFilterBarFiltersToContext(filterBarFilters, SOURCE);
      replaceFiltersForSource(SOURCE, newFilters);
    },
    [replaceFiltersForSource],
  );

  const _clearFilters = useCallback(() => {
    setAppliedFilterBarFilters([]);
    replaceFiltersForSource(SOURCE, []);
  }, [replaceFiltersForSource]);

  useEffect(() => {
    _onFiltersChanged(defaultFilters);
  }, [defaultFilters, _onFiltersChanged]);

  if (isHidden) {
    return null;
  }

  // This makes a whole new instance of the filter bar component every time, so we get flickering as things re-render. Need to clean that up. The problem is that the render-cycle for overflow is out of order when changing allFilterBys, leading to rendering the filterBys using an outdated visibleIndices array.
  const FilterBarComponent = () => (
    <FilterBar
      allFilterBys={allFilterBys}
      appliedFilters={appliedFilterBarFilters}
      clearFilters={_clearFilters}
      criteriaArgumentsMap={correctedCriteriaArgumentsMap}
      criteriaOperatorsMap={criteriaOperatorsMap}
      onFiltersChanged={_onFiltersChanged}
      popularFilterBys={allFilterBys}
    />
  );

  return (
    <div className={style['filter-bar-wrapper']}>
      <FilterBarComponent />
    </div>
  );
};

BatchesFilterBar.propTypes = {
  isHidden: PropTypes.bool.isRequired,
  allFilterBys: PropTypes.arrayOf(PropTypes.object),
  defaultFilters: PropTypes.arrayOf(PropTypes.object),
};

export default BatchesFilterBar;
